import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { useAuthStore } from '@/features/auth/store/authStore';
import type {
  AuthorizationContextValue,
  AuthorizationState,
  UserRole,
} from '@/features/auth/types/authorization';
import {
  can as checkCan,
  cannot as checkCannot,
  hasRole as checkHasRole,
  hasAnyRole as checkHasAnyRole,
  hasAllRoles as checkHasAllRoles,
  hasAnyPermission as checkHasAnyPermission,
  hasAllPermissions as checkHasAllPermissions,
  isSuperAdmin as checkIsSuperAdmin,
} from '@/features/auth/utils/permission-utils';
import type { PermissionName } from '@/features/auth/constants';
import type { RoleName } from '@/features/auth/constants';

// ─── Context ──────────────────────────────────────────────────────────

const AuthorizationContext = createContext<AuthorizationContextValue | null>(
  null,
);

// ─── API Call to Fetch Permissions ────────────────────────────────────
//
// Calls GET /auth/permissions which returns the user's roles
// and effective permissions from the backend.

async function fetchUserPermissions(): Promise<{
  roles: UserRole[];
  permissions: string[];
}> {
  try {
    const { default: apiClient } = await import('@/shared/lib/apiClient');
    const response = await apiClient.get<{
      success: boolean;
      data: {
        userId: string;
        email: string;
        roles: UserRole[];
        permissions: string[];
        isSuperAdmin: boolean;
      };
    }>('/auth/permissions');

    const data = response.data?.data;

    return {
      roles: data?.roles ?? [],
      permissions: data?.permissions ?? [],
    };
  } catch {
    return { roles: [], permissions: [] };
  }
}

// ─── Provider ─────────────────────────────────────────────────────────

interface AuthorizationProviderProps {
  children: ReactNode;
}

export function AuthorizationProvider({
  children,
}: AuthorizationProviderProps) {
  const { user, status } = useAuthStore();

  const [state, setState] = useState<AuthorizationState>({
    roles: [],
    permissionNames: [],
    isLoaded: false,
    isLoading: false,
    error: null,
  });

  const refresh = useCallback(async () => {
    if (status !== 'authenticated' || !user) {
      setState({
        roles: [],
        permissionNames: [],
        isLoaded: true,
        isLoading: false,
        error: null,
      });
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const data = await fetchUserPermissions();
      setState({
        roles: data.roles,
        permissionNames: data.permissions,
        isLoaded: true,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      setState({
        roles: [],
        permissionNames: [],
        isLoaded: true,
        isLoading: false,
        error: err instanceof Error ? err : new Error('Failed to load permissions'),
      });
    }
  }, [status, user]);

  // Refresh on auth state changes
  useEffect(() => {
    if (status === 'authenticated' && user) {
      refresh();
    } else if (status === 'unauthenticated') {
      setState({
        roles: [],
        permissionNames: [],
        isLoaded: true,
        isLoading: false,
        error: null,
      });
    }
  }, [status, user, refresh]);

  // ─── Role Names (extracted for convenience) ─────────────────────

  const userRoleNames = useMemo(
    () => state.roles.map((r) => r.roleName),
    [state.roles],
  );

  // ─── Authorization Methods ──────────────────────────────────────

  const can = useCallback(
    (permission: PermissionName | string) =>
      checkCan(permission, state.permissionNames),
    [state.permissionNames],
  );

  const cannot = useCallback(
    (permission: PermissionName | string) =>
      checkCannot(permission, state.permissionNames),
    [state.permissionNames],
  );

  const hasRole = useCallback(
    (role: RoleName | string) => checkHasRole(role, userRoleNames),
    [userRoleNames],
  );

  const hasAnyRole = useCallback(
    (...roles: (RoleName | string)[]) => checkHasAnyRole(roles, userRoleNames),
    [userRoleNames],
  );

  const hasAllRoles = useCallback(
    (...roles: (RoleName | string)[]) => checkHasAllRoles(roles, userRoleNames),
    [userRoleNames],
  );

  const hasPermission = useCallback(
    (permission: PermissionName | string) =>
      checkCan(permission, state.permissionNames),
    [state.permissionNames],
  );

  const hasAnyPermission = useCallback(
    (...permissions: (PermissionName | string)[]) =>
      checkHasAnyPermission(permissions, state.permissionNames),
    [state.permissionNames],
  );

  const hasAllPermissions = useCallback(
    (...permissions: (PermissionName | string)[]) =>
      checkHasAllPermissions(permissions, state.permissionNames),
    [state.permissionNames],
  );

  const isSuperAdmin = useCallback(
    () => checkIsSuperAdmin(userRoleNames),
    [userRoleNames],
  );

  // ─── Memoized Context Value ─────────────────────────────────────

  const contextValue = useMemo<AuthorizationContextValue>(
    () => ({
      roles: state.roles,
      permissionNames: state.permissionNames,
      isLoaded: state.isLoaded,
      isLoading: state.isLoading,
      error: state.error,
      refresh,
      can,
      cannot,
      hasRole,
      hasAnyRole,
      hasAllRoles,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      isSuperAdmin,
    }),
    [
      state.roles,
      state.permissionNames,
      state.isLoaded,
      state.isLoading,
      state.error,
      refresh,
      can,
      cannot,
      hasRole,
      hasAnyRole,
      hasAllRoles,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      isSuperAdmin,
    ],
  );

  return (
    <AuthorizationContext.Provider value={contextValue}>
      {children}
    </AuthorizationContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────

export function useAuthorization(): AuthorizationContextValue {
  const ctx = useContext(AuthorizationContext);
  if (!ctx) {
    throw new Error(
      'useAuthorization must be used within an AuthorizationProvider',
    );
  }
  return ctx;
}
